import { useEffect, useMemo } from 'react'

import { useRequest } from 'ahooks'
import { isString } from 'lodash'

import { queryFileDetailApi } from '@eastcoal/api/nexus/oss/queryFileDetail'

import { ObjectAcl } from '@eastcoal/common/enums/ObjectAcl'
import { KeySet } from '@eastcoal/common/types/utilities'
import { wrapUploadFileApi } from '@eastcoal/common/utils/upload'

import { useImageCardStoreApi } from '../hooks/useStore'
import { ImageCardAction, ImageCardState } from '../store/interface'

import { ImageCardStoreUpdaterProps } from './interface'

const useStoreUpdater = <K extends KeySet<ImageCardState>>(
  key: K,
  value: ImageCardState[K]
) => {
  const { setState } = useImageCardStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <K extends KeySet<ImageCardAction>>(
  key: K,
  value: Parameters<ImageCardAction[K]>,
  refreshDeps: any[]
) => {
  const { getState } = useImageCardStoreApi()

  useEffect(() => {
    ;(getState()[key] as any)(...value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getState, key, ...refreshDeps])
}

const requstConfig = {
  timeout: 5 * 60 * 1000
}

export const ImageCardStoreUpdater = ({
  nodeKey,
  file,
  ossId: externalOssId,
  filename: externalFilename,
  error: externalError,
  rotate,
  align,
  bordered,
  shadowed,
  link,
  desc,
  ossUrl: externalOsssUrl,
  descInputVisible,
  currentHeight,
  currentWidth
}: ImageCardStoreUpdaterProps) => {
  const {
    data: uploadData,
    error: UploadError,
    loading: uploadLoading
  } = useRequest(
    () =>
      wrapUploadFileApi(
        { file, objectAcl: ObjectAcl.PUBLIC_READ },
        requstConfig
      ),
    {
      manual: false,
      ready: !!file && !externalOssId,
      refreshDeps: [file]
    }
  )

  const {
    data: queryData,
    error: queryError,
    loading: queryLoading
  } = useRequest(
    () => queryFileDetailApi({ ossId: externalOssId }, requstConfig),
    {
      manual: false,
      ready: !externalOsssUrl && !!externalOssId && !file,
      refreshDeps: [externalOssId],
      cacheKey: externalOssId,
      staleTime: Infinity
    }
  )

  const data = uploadData ?? queryData
  const error = UploadError ?? queryError
  const loading = uploadLoading ?? queryLoading

  const ossUrl = useMemo(() => {
    if (externalOsssUrl) {
      return externalOsssUrl
    }
    if (data) {
      return data.accessUrl
    }
    if (file) {
      return isString(file) ? file : URL.createObjectURL(file)
    }
    return undefined
  }, [data, file, externalOsssUrl])

  useStoreUpdater('ossId', data?.ossId ?? externalOssId)
  useStoreUpdater('ossUrl', ossUrl)
  useStoreUpdater('filename', data?.filename ?? externalFilename)
  useStoreUpdater('error', error?.message ?? externalError)
  useStoreUpdater('loading', loading)
  useStoreUpdater('nodeKey', nodeKey)
  useStoreUpdater('rotate', rotate)
  useStoreUpdater('align', align)
  useStoreUpdater('bordered', bordered)
  useStoreUpdater('shadowed', shadowed)
  useStoreUpdater('desc', desc)
  useStoreUpdater('link', link)

  useStoreSetter(
    'toggleDescInputVisible',
    [!!descInputVisible],
    [descInputVisible]
  )
  useStoreSetter(
    'setCurrentSize',
    [
      {
        height: currentHeight,
        width: currentWidth
      }
    ],
    [currentHeight, currentWidth]
  )

  return null
}
